import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import { Phone, ShieldCheck } from "lucide-react";

export default function PatientLoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/patient";
  const { saveSession } = useAuth();
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [demoOtp, setDemoOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(phone)) { toast.error("Enter a 10-digit mobile number."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/patient/request-otp", { phone, name: name || undefined });
      setDemoOtp(data.demo_otp);
      setStep("otp");
      toast.success("OTP generated.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) { toast.error("Enter the 6-digit OTP."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/patient/verify-otp", { phone, code });
      saveSession(data.token, data.user);
      toast.success("Welcome!");
      navigate(next);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mx-auto max-w-[460px] px-4 py-16">
      <Seo title="Patient login" canonicalPath="/patient/login" />
      <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">← Back to home</Link>
      <div className="mt-6 rounded-2xl border border-border p-7 shadow-sm">
        <span className="inline-flex h-12 w-12 rounded-xl bg-accent text-[hsl(var(--ld-jade-fg))] items-center justify-center">
          <Phone size={22} />
        </span>
        <h1 className="font-display text-2xl font-bold mt-4">Log in with your mobile</h1>
        <p className="text-muted-foreground text-sm mt-1">We'll generate an OTP so you can book doctors and skip the queue.</p>

        {step === "phone" ? (
          <form onSubmit={requestOtp} className="mt-6 grid gap-3" data-testid="phone-form">
            <input
              className="input-base tabular"
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="patient-name"
            />
            <div className="flex items-stretch gap-2">
              <span className="input-base !w-auto !flex items-center font-semibold text-muted-foreground">+91</span>
              <input
                className="input-base tabular flex-1"
                placeholder="10-digit mobile number"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                inputMode="numeric"
                data-testid="patient-phone"
              />
            </div>
            <button type="submit" disabled={loading} className="btn-primary" data-testid="request-otp-btn">
              {loading ? "Sending…" : "Send OTP"}
            </button>
          </form>
        ) : (
          <form onSubmit={verifyOtp} className="mt-6 grid gap-3" data-testid="otp-form">
            <div className="rounded-lg bg-accent text-[hsl(var(--ld-jade-fg))] text-sm p-3 flex items-start gap-2">
              <ShieldCheck size={18} className="mt-0.5 shrink-0" />
              <p>
                SMS is not enabled in this MVP, so your OTP is shown here:
                <span className="block mt-1 font-display text-2xl font-extrabold tabular tracking-widest" data-testid="demo-otp-display">{demoOtp}</span>
              </p>
            </div>
            <input
              className="input-base tabular text-center text-xl tracking-widest font-bold"
              placeholder="------"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              data-testid="otp-input"
            />
            <button type="submit" disabled={loading} className="btn-primary" data-testid="verify-otp-btn">
              {loading ? "Verifying…" : "Verify & continue"}
            </button>
            <button type="button" onClick={() => { setStep("phone"); setCode(""); }} className="btn-ghost text-xs" data-testid="back-to-phone">
              Use a different number
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
